import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';
import type { PackageOfferUnit } from './SeekerDetailModal/types';
import { useGuideTimezone } from '../hooks/useGuideTimezone';
import { formatTimeInTimezone, getTimezoneAbbreviation } from '../utils/timezoneUtils';

type ScheduledSessionRow = {
  id: string;
  session_date: string;
  session_time: string;
  start_time?: string; // UTC timestamp
  duration_minutes: number | null;
  session_status: string | null;
};

type ExistingSession = ScheduledSessionRow | null;

type AvailabilitySlot = {
  time: string; // Legacy HH:MM
  available: boolean;
  // Timezone-aware fields
  start_utc?: string;
  end_utc?: string;
  guide_timezone?: string;
};

type AvailabilityDay = {
  date: string;
  dayOfWeek?: string;
  timeSlots: AvailabilitySlot[];
};

interface ScheduleUnitModalProps {
  isOpen: boolean;
  onClose: () => void;
  guideId: string | null;
  seekerId: string | null;
  seekerName?: string | null;
  seekerEmail?: string | null;
  unit: PackageOfferUnit | null;
  onScheduled: (sessionId: string, options: { rescheduled: boolean }) => void;
}

const COMPONENT_TYPE_TO_SESSION_TYPE: Record<string, 'consultation' | 'preparation' | 'integration' | 'journey' | 'follow_up'> = {
  preparation: 'preparation',
  integration: 'integration',
  ceremony: 'journey',
  additional: 'follow_up',
  journey: 'journey',
  follow_up: 'follow_up',
  consultation: 'consultation',
};

const SESSION_TYPE_LABEL: Record<string, string> = {
  consultation: 'Consultation',
  preparation: 'Preparation Session',
  integration: 'Integration Session',
  journey: 'Journey Session',
  follow_up: 'Follow Up Session',
};

const DEFAULT_DURATION_MINUTES = 60;
const AVAILABILITY_WINDOW_DAYS = 30;

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (date: string): string => {
  try {
    const dateObj = new Date(`${date}T00:00:00`);
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return date;
  }
};

const formatTimeForDisplay = (slot: AvailabilitySlot, guideTimezone: string): string => {
  // Prefer UTC timestamp if available
  if (slot.start_utc && slot.guide_timezone) {
    const timeStr = formatTimeInTimezone(slot.start_utc, guideTimezone);
    const tzAbbrev = getTimezoneAbbreviation(slot.start_utc, guideTimezone);
    return tzAbbrev ? `${timeStr} ${tzAbbrev}` : timeStr;
  }

  // Fallback to legacy time string
  const [hours, minutes] = slot.time.split(':').map((value) => Number.parseInt(value, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return slot.time;
  }
  const dateObj = new Date();
  dateObj.setHours(hours, minutes, 0, 0);
  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatTimeStringForDisplay = (time: string): string => {
  const [hours, minutes] = time.split(':').map((value) => Number.parseInt(value, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return time;
  }
  const dateObj = new Date();
  dateObj.setHours(hours, minutes, 0, 0);
  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const normalizeTimeToHHMM = (time: string | null | undefined): string | null => {
  if (!time) return null;
  const parts = time.split(':');
  if (parts.length < 2) return null;
  const [hour, minute] = parts;
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
};

const resolveDurationMinutes = (unit: PackageOfferUnit | null, existingSession: ExistingSession): number => {
  if (typeof unit?.durationMinutes === 'number' && unit.durationMinutes > 0) {
    return unit.durationMinutes;
  }

  if (typeof existingSession?.duration_minutes === 'number' && existingSession.duration_minutes > 0) {
    return existingSession.duration_minutes;
  }

  const componentKey =
    typeof unit?.componentType === 'string'
      ? unit.componentType.toLowerCase()
      : '';
  switch (componentKey) {
    case 'preparation':
    case 'integration':
    case 'follow_up':
    case 'additional':
      return 60;
    case 'ceremony':
    case 'journey':
      return 240;
    case 'consultation':
      return 30;
    default:
      return DEFAULT_DURATION_MINUTES;
  }
};

const getSessionTypeForUnit = (unit: PackageOfferUnit | null): 'consultation' | 'preparation' | 'integration' | 'journey' | 'follow_up' => {
  const key = unit?.componentType?.toLowerCase() ?? '';
  return COMPONENT_TYPE_TO_SESSION_TYPE[key] ?? 'consultation';
};

const sortAvailabilityDays = (days: AvailabilityDay[]): AvailabilityDay[] => {
  return [...days].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
};

const canFitSession = (
  timeSlots: AvailabilitySlot[],
  startTime: string,
  durationMinutes: number
): boolean => {
  // Find the index of the start time
  const startIndex = timeSlots.findIndex(slot => slot.time === startTime);
  if (startIndex === -1 || !timeSlots[startIndex].available) {
    return false;
  }

  // Calculate how many 15-minute slots we need (assuming slots are 15 minutes apart)
  const slotsNeeded = Math.ceil(durationMinutes / 15);

  // Check if we have enough consecutive available slots
  for (let i = 0; i < slotsNeeded; i++) {
    const slotIndex = startIndex + i;
    if (slotIndex >= timeSlots.length || !timeSlots[slotIndex].available) {
      return false;
    }
  }

  return true;
};

const ScheduleUnitModal: React.FC<ScheduleUnitModalProps> = ({
  isOpen,
  onClose,
  guideId,
  seekerId,
  seekerName,
  seekerEmail,
  unit,
  onScheduled,
}) => {
  const { timezone: guideTimezone } = useGuideTimezone();
  const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedStartUtc, setSelectedStartUtc] = useState<string>(''); // NEW: Track selected UTC time
  const [locationKind, setLocationKind] = useState<'online' | 'in_person' | 'link'>('online');
  const [locationDetails, setLocationDetails] = useState('');
  const [sessionLink, setSessionLink] = useState('');
  const [existingSession, setExistingSession] = useState<ExistingSession>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayedMonth, setDisplayedMonth] = useState<Date>(() => new Date());
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const durationMinutes = useMemo(
    () => resolveDurationMinutes(unit, existingSession),
    [unit, existingSession],
  );

  const sessionType = useMemo(
    () => getSessionTypeForUnit(unit),
    [unit],
  );

  const normalizedToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const startOfCalendar = useMemo(() => {
    const firstOfMonth = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1);
    const start = new Date(firstOfMonth);
    start.setDate(start.getDate() - start.getDay());
    return start;
  }, [displayedMonth]);

  const goToPrevMonth = () => {
    setDisplayedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setDisplayedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const canGoPrevMonth = useMemo(() => {
    const prevMonth = new Date(displayedMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const currentMonth = new Date(normalizedToday.getFullYear(), normalizedToday.getMonth(), 1);
    const prevMonthStart = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
    return prevMonthStart >= currentMonth;
  }, [displayedMonth, normalizedToday]);

  const calendarDays = useMemo(() => {
    const days: Array<{
      dateKey: string;
      label: number;
      isPast: boolean;
      hasSlots: boolean;
      isSelected: boolean;
      isCurrentMonth: boolean;
    }> = [];

    for (let i = 0; i < 42; i += 1) {
      const currentDate = new Date(startOfCalendar);
      currentDate.setDate(startOfCalendar.getDate() + i);
      const dateKey = formatDateKey(currentDate);
      const isPast = currentDate < normalizedToday && dateKey !== formatDateKey(normalizedToday);
      const isCurrentMonth = currentDate.getMonth() === displayedMonth.getMonth();
      const dayAvailability = availability.find((day) => day.date === dateKey);
      // Check if there are any slots that can fit the full session duration
      const hasSlots = dayAvailability?.timeSlots?.some((slot) =>
        canFitSession(dayAvailability.timeSlots, slot.time, durationMinutes)
      ) ?? false;
      const isSelected = selectedDate === dateKey;

      days.push({
        dateKey,
        label: currentDate.getDate(),
        isPast,
        hasSlots,
        isSelected,
        isCurrentMonth,
      });
    }

    return days;
  }, [availability, normalizedToday, selectedDate, startOfCalendar, durationMinutes, displayedMonth]);

  const selectedDaySlots = useMemo(() => {
    if (!selectedDate) return [];
    const daySlots = availability.find((day) => day.date === selectedDate)?.timeSlots ?? [];

    // Filter slots to only show those that can fit the full session duration
    return daySlots.map((slot) => ({
      ...slot,
      available: slot.available && canFitSession(daySlots, slot.time, durationMinutes),
    }));
  }, [availability, selectedDate, durationMinutes]);

  const selectedDayHasSlots = selectedDaySlots.length > 0;
  const selectedDayAllUnavailable = selectedDayHasSlots && selectedDaySlots.every((slot) => !slot.available);
  const availabilityHasOptions = availability.some((day) =>
    day.timeSlots.some((slot) => canFitSession(day.timeSlots, slot.time, durationMinutes))
  );
  const existingSessionLabel =
    existingSession?.session_date && existingSession?.session_time
      ? `${formatDateForDisplay(existingSession.session_date)} at ${
          formatTimeStringForDisplay(
            normalizeTimeToHHMM(existingSession.session_time) ?? existingSession.session_time,
          )
        }`
      : null;
  const locationDetailsRequired = locationKind === 'in_person';
  const sessionLinkRequired = locationKind === 'link';

  useEffect(() => {
    if (!isOpen) {
      setAvailability([]);
      setSelectedDate('');
      setSelectedTime('');
      setSelectedStartUtc('');
      setExistingSession(null);
      setError(null);
      setLoadingAvailability(false);
      setIsSaving(false);
      setLocationKind('online');
      setLocationDetails('');
      setSessionLink('');
      setDisplayedMonth(new Date());
      setInitialLoadComplete(false);
    }
  }, [isOpen]);

  // Initial data load (existing session + first availability fetch)
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!isOpen || !guideId || !unit) {
        return;
      }

      setLoadingAvailability(true);
      setError(null);

      try {
        const supabase = getSupabaseClient();
        let fetchedExistingSession: (ScheduledSessionRow & { location?: 'online' | 'in_person' | 'link'; location_details?: string | null; session_link?: string | null }) | null = null;

        if (unit.scheduledSessionId) {
          const { data: existingRow, error: existingError } = await supabase
            .from('scheduled_sessions')
            .select('id, session_date, session_time, duration_minutes, session_status, location, location_details, session_link')
            .eq('id', unit.scheduledSessionId)
            .maybeSingle();

          if (existingError) {
            console.warn('Unable to load existing session for unit', existingError);
          } else if (existingRow) {
            fetchedExistingSession = existingRow as any;
            setLocationKind((existingRow.location as 'online' | 'in_person' | 'link') ?? 'online');
            setLocationDetails(existingRow.location_details ?? '');
            setSessionLink(existingRow.session_link ?? '');
          } else {
            setLocationKind('online');
            setLocationDetails('');
            setSessionLink('');
          }
        } else {
          // No existing session — check if the component has a default session_link
          if (unit.templateComponentId) {
            const { data: componentRow } = await supabase
              .from('guide_package_components')
              .select('session_link')
              .eq('id', unit.templateComponentId)
              .maybeSingle();

            if (componentRow?.session_link) {
              setLocationKind('link');
              setSessionLink(componentRow.session_link);
            } else {
              setLocationKind('online');
              setSessionLink('');
            }
          } else {
            setLocationKind('online');
            setSessionLink('');
          }
          setLocationDetails('');
        }

        const env = (window as any).__ENV__;
        const baseUrl = env?.SUPABASE_URL;
        if (!baseUrl) {
          throw new Error('Missing Supabase URL for availability.');
        }

        const today = new Date();
        const startDate = today.toISOString().split('T')[0];
        const endDateObj = new Date(today.getTime() + AVAILABILITY_WINDOW_DAYS * 24 * 60 * 60 * 1000);
        const endDate = endDateObj.toISOString().split('T')[0];

        const response = await fetch(
          `${baseUrl}/functions/v1/guide-availability?guide_id=${guideId}&start_date=${startDate}&end_date=${endDate}`
        );

        if (!response.ok) {
          throw new Error('Unable to load guide availability.');
        }

        const data = await response.json();
        const availabilityDays: AvailabilityDay[] = sortAvailabilityDays(data.availability ?? []);

        if (cancelled) return;

        setExistingSession(fetchedExistingSession);
        setAvailability(availabilityDays);
        setInitialLoadComplete(true);

        const existingDate = fetchedExistingSession?.session_date ?? '';
        const existingTime = normalizeTimeToHHMM(fetchedExistingSession?.session_time) ?? '';

        if (existingDate) {
          setSelectedDate(existingDate);
          setSelectedTime(existingTime);
        } else {
          const firstAvailableDay = availabilityDays.find((day) =>
            day.timeSlots.some((slot) => slot.available),
          );
          if (firstAvailableDay) {
            setSelectedDate(firstAvailableDay.date);
            const firstAvailableSlot = firstAvailableDay.timeSlots.find((slot) => slot.available);
            setSelectedTime(firstAvailableSlot ? firstAvailableSlot.time : '');
          } else {
            setSelectedDate('');
            setSelectedTime('');
          }
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load availability.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoadingAvailability(false);
        }
      }
    };

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [isOpen, guideId, unit, durationMinutes]);

  // Fetch availability when navigating to a different month
  useEffect(() => {
    // Skip if initial load hasn't completed yet (to avoid duplicate requests)
    if (!initialLoadComplete || !isOpen || !guideId) {
      return;
    }

    let cancelled = false;

    const fetchMonthAvailability = async () => {
      const env = (window as any).__ENV__;
      const baseUrl = env?.SUPABASE_URL;
      if (!baseUrl) {
        return;
      }

      // Format month as YYYY-MM for the API
      const year = displayedMonth.getFullYear();
      const month = String(displayedMonth.getMonth() + 1).padStart(2, '0');
      const monthParam = `${year}-${month}`;

      setLoadingAvailability(true);

      try {
        const response = await fetch(
          `${baseUrl}/functions/v1/guide-availability?guide_id=${guideId}&month=${monthParam}`
        );

        if (!response.ok) {
          throw new Error('Unable to load guide availability for selected month.');
        }

        const data = await response.json();
        const newAvailabilityDays: AvailabilityDay[] = sortAvailabilityDays(data.availability ?? []);

        if (cancelled) return;

        // Merge new availability with existing, replacing overlapping dates
        setAvailability((prev) => {
          const existingMap = new Map(prev.map((day) => [day.date, day]));

          // Add/replace with new data
          for (const day of newAvailabilityDays) {
            existingMap.set(day.date, day);
          }

          // Convert back to array and sort
          return sortAvailabilityDays(Array.from(existingMap.values()));
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch availability for month:', err);
          // Don't set error state here - just log it, as we still have existing data
        }
      } finally {
        if (!cancelled) {
          setLoadingAvailability(false);
        }
      }
    };

    void fetchMonthAvailability();

    return () => {
      cancelled = true;
    };
  }, [displayedMonth, initialLoadComplete, isOpen, guideId]);

  const handleSelectDate = (value: string) => {
    setSelectedDate(value);
    setSelectedTime('');
    setSelectedStartUtc(''); // Clear UTC time when date changes
    setError(null);
  };

  const handleSelectTime = (slot: AvailabilitySlot, available: boolean) => {
    if (!available) return;

    // Store the UTC time for comparison and submission
    if (slot.start_utc) {
      setSelectedStartUtc(slot.start_utc);
    } else {
      setSelectedStartUtc('');
    }

    // Store slot.time to match isSelected comparison (which compares against slot.time)
    setSelectedTime(slot.time);

    setError(null);
  };

  const handleSubmit = async () => {
    if (!guideId || !seekerId || !unit) {
      setError('Missing required information to schedule this session.');
      return;
    }

    if (!selectedDate || !selectedTime) {
      setError('Select a date and time before scheduling.');
      return;
    }

    if (locationKind === 'in_person' && !locationDetails.trim()) {
      setError('Provide location details for in-person sessions.');
      return;
    }

    if (locationKind === 'link' && !sessionLink.trim()) {
      setError('Provide a meeting link for link sessions.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      if (unit.scheduledSessionId) {
        // Reschedule existing session
        const { data, error } = await supabase.functions.invoke('session-reschedule', {
          body: {
            session_id: unit.scheduledSessionId,
            // Keep legacy fields for backward compatibility
            new_date: selectedDate,
            new_time: `${selectedTime}:00`,
            // NEW: Include UTC timestamp if available
            start_utc: selectedStartUtc || undefined,
            location: locationKind,
            location_details: locationKind === 'in_person' ? locationDetails.trim() : undefined,
            session_link: locationKind === 'link' ? sessionLink.trim() : undefined,
          },
        });

        if (error) {
          throw new Error(error.message || 'Failed to reschedule session');
        }

        console.log('Session rescheduled:', data);
        onScheduled(unit.scheduledSessionId, { rescheduled: true });
      } else {
        // Schedule new session
        const { data, error } = await supabase.functions.invoke('session-schedule', {
          body: {
            guide_id: guideId,
            seeker_id: seekerId,
            session_type: sessionType,
            // Keep legacy fields for backward compatibility
            session_date: selectedDate,
            session_time: `${selectedTime}:00`,
            // NEW: Include UTC timestamp if available
            start_utc: selectedStartUtc || undefined,
            duration_minutes: durationMinutes,
            seeker_name: seekerName || null,
            seeker_email: seekerEmail || null,
            notes: unit.componentName ? `Package unit: ${unit.componentName}` : null,
            location: locationKind,
            location_details: locationKind === 'in_person' ? locationDetails.trim() : undefined,
            session_link: locationKind === 'link' ? sessionLink.trim() : undefined,
            package_offer_unit_id: unit.id,
          },
        });

        if (error) {
          throw new Error(error.message || 'Failed to schedule session');
        }

        console.log('Session scheduled:', data);
        onScheduled(data.session_id, { rescheduled: false });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save session.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const sessionTypeLabel = SESSION_TYPE_LABEL[sessionType] ?? 'Session';
  const monthLabel = displayedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const primaryActionLabel = unit?.scheduledSessionId ? 'Reschedule Session' : 'Schedule Session';
  const disablePrimaryAction =
    isSaving ||
    !guideId ||
    !seekerId ||
    !unit ||
    !selectedDate ||
    !selectedTime ||
    (locationDetailsRequired && !locationDetails.trim()) ||
    (sessionLinkRequired && !sessionLink.trim());

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#2C2731]/80 px-3 py-6">
      <div className="w-full max-w-[560px]">
        <div className="flex max-h-[92vh] flex-col overflow-hidden rounded-[32px] border border-[#2C2731] bg-[#FFFCEF] shadow-[0_30px_120px_-40px_rgba(32,77,60,0.65)]">
          <header className="flex h-16 items-center border-b border-[#2C2731]/30 bg-[#2C2731] px-4">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
              aria-label="Close"
            >
              {'\u2190'}
            </button>
            <div className="flex flex-1 justify-center">
              <h3 className="text-lg font-semibold text-[#FFFCEF]">{primaryActionLabel}</h3>
            </div>
            <div className="w-11" aria-hidden />
          </header>

          <main className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-6">
            {error && (
              <div className="rounded-2xl border border-[#F87171]/60 bg-white/90 px-4 py-3 text-sm text-[#7A1E1E]">
                {error}
              </div>
            )}

            {(!guideId || !seekerId || !unit) && (
              <div className="rounded-2xl border border-[#F97316]/50 bg-white/85 px-4 py-3 text-sm text-[#7C4A03]">
                Invite this seeker to create an account before scheduling package sessions.
              </div>
            )}

            <section className="rounded-[32px] border border-[#2C2731]/20 bg-white/90 p-6 shadow-[0_32px_60px_-35px_rgba(32,77,60,0.45)]">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                Session Details
              </p>
              <h1 className="mt-2 text-xl font-semibold text-[#2C2731]">
                {unit?.componentName ?? 'Package Session'}
              </h1>
              <p className="mt-1 text-sm text-[#2C2731]/70">
                {sessionTypeLabel} · {durationMinutes} minutes
                {existingSessionLabel ? ` · Currently ${existingSessionLabel}` : ''}
              </p>
            </section>

            <section className="rounded-3xl border border-[#2C2731]/20 bg-white/85 p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">Location</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {[
                  { value: 'online' as const, label: 'Online' },
                  { value: 'in_person' as const, label: 'In person' },
                  { value: 'link' as const, label: 'Link' },
                ].map((option) => {
                  const isActive = locationKind === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setLocationKind(option.value)}
                      className={[
                        'rounded-2xl border px-4 py-3 text-sm font-semibold transition',
                        isActive
                          ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                          : 'border-[#2C2731]/25 bg-[#FFFCEF] text-[#2C2731] hover:border-[#2C2731] hover:bg-[#2C2731] hover:text-[#FFFCEF]',
                      ].join(' ')}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {locationKind === 'link' && (
                <div className="mt-4">
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/60">
                    Meeting link
                  </label>
                  <input
                    type="url"
                    value={sessionLink}
                    onChange={(event) => setSessionLink(event.target.value)}
                    placeholder="Paste Zoom, Google Meet, or other meeting link"
                    className="mt-2 w-full rounded-2xl border border-[#2C2731]/20 bg-white px-4 py-3 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none"
                  />
                </div>
              )}
              {locationKind === 'in_person' && (
                <div className="mt-4">
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/60">
                    Location details
                  </label>
                  <textarea
                    value={locationDetails}
                    onChange={(event) => setLocationDetails(event.target.value)}
                    rows={3}
                    placeholder="Add address, entrance instructions, or parking notes"
                    className="mt-2 w-full rounded-2xl border border-[#2C2731]/20 bg-white px-4 py-3 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none"
                  />
                  <p className="mt-2 text-xs text-[#2C2731]/60">
                    Only you can see these details. Share with the seeker once confirmed.
                  </p>
                </div>
              )}
            </section>

            {loadingAvailability ? (
              <div className="rounded-[28px] border border-[#2C2731]/15 bg-white/80 p-6">
                <div className="h-4 w-2/3 animate-pulse rounded-full bg-[#E8DFBD]" />
                <div className="mt-4 h-40 animate-pulse rounded-[24px] bg-[#E8DFBD]" />
              </div>
            ) : availabilityHasOptions ? (
              <>
                <div className="rounded-3xl border border-[#2C2731]/15 bg-white/85 p-6">
                  <div className="flex items-center justify-between text-[#2C2731]">
                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                      Select a date
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={goToPrevMonth}
                        disabled={!canGoPrevMonth}
                        className="rounded-full p-1 text-[#2C2731]/70 transition hover:bg-[#2C2731]/10 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Previous month"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      <span className="min-w-[140px] text-center text-sm font-medium text-[#2C2731]/70">{monthLabel}</span>
                      <button
                        type="button"
                        onClick={goToNextMonth}
                        className="rounded-full p-1 text-[#2C2731]/70 transition hover:bg-[#2C2731]/10"
                        aria-label="Next month"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-[#2C2731]/60">All times shown in {guideTimezone}</p>
                  <div className="mt-4 grid grid-cols-7 gap-2 text-center text-xs font-semibold text-[#2C2731]/70">
                    {DAY_LABELS.map((label) => (
                      <div key={label}>{label}</div>
                    ))}
                  </div>
                  <div className="mt-2 grid grid-cols-7 gap-2">
                    {calendarDays.map((day) => (
                      <button
                        key={day.dateKey}
                        type="button"
                        onClick={() => (!day.isPast && day.hasSlots ? handleSelectDate(day.dateKey) : null)}
                        disabled={day.isPast || !day.hasSlots}
                        className={[
                          'h-12 rounded-2xl border text-sm font-semibold transition disabled:cursor-not-allowed',
                          day.isSelected
                            ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                            : day.hasSlots && day.isCurrentMonth && !day.isPast
                              ? 'border-[#2C2731]/20 bg-[#FFFCEF] text-[#2C2731] hover:border-[#2C2731]'
                              : 'border-transparent bg-transparent text-[#C7C1AD]',
                        ].join(' ')}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedDate && (
                  <div className="rounded-3xl border border-[#2C2731]/15 bg-[#FFFCEF] p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2C2731]/70">
                      Available times
                    </p>
                    {selectedDayHasSlots ? (
                      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {selectedDaySlots.map((slot) => {
                          // Check if this slot is selected by comparing UTC time or legacy time
                          const isSelected = slot.available && (
                            (slot.start_utc && selectedStartUtc && slot.start_utc === selectedStartUtc) ||
                            (!slot.start_utc && selectedTime === slot.time)
                          );
                          return (
                            <button
                              key={slot.start_utc || slot.time}
                              type="button"
                              onClick={() => handleSelectTime(slot, slot.available)}
                              disabled={!slot.available}
                              className={[
                                'rounded-full border px-4 py-3 text-sm font-semibold transition',
                                isSelected
                                  ? 'border-[#2C2731] bg-[#2C2731] text-[#FFFCEF]'
                                  : slot.available
                                    ? 'border-[#2C2731]/20 bg-white text-[#2C2731] hover:border-[#2C2731]'
                                    : 'cursor-not-allowed border-[#C7C1AD]/40 bg-[#F4EAD1] text-[#C7C1AD] line-through',
                              ].join(' ')}
                            >
                              {formatTimeForDisplay(slot, guideTimezone)}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-[#2C2731]/70">
                        No available times for this date.
                      </p>
                    )}

                    {selectedDayAllUnavailable && (
                      <p className="mt-4 text-sm text-[#2C2731]/60">
                        Every slot is already booked. Choose another date.
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="rounded-3xl border border-[#2C2731]/20 bg-white/80 px-5 py-4 text-sm text-[#2C2731]/70">
                There are no open windows in the next {AVAILABILITY_WINDOW_DAYS} days. Update your calendar blocks to
                continue.
              </p>
            )}
          </main>

          <footer className="flex items-center gap-3 border-t border-[#2C2731]/20 bg-gradient-to-t from-[#FFFCEF] via-[#FFFCEF] to-[#FFFCEF]/0 px-6 pb-6 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#2C2731]/40 bg-[#FFFCEF] px-6 py-4 text-lg font-medium text-[#2C2731] transition hover:-translate-y-0.5 hover:border-[#2C2731]"
            >
              <span className="w-5 text-xl leading-none">{'\u2190'}</span>
              <span>Cancel</span>
              <span className="invisible w-5 text-xl leading-none">{'\u2192'}</span>
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={disablePrimaryAction}
              className="group inline-flex w-full items-center justify-between gap-3 rounded-full border border-[#2C2731] bg-[#2C2731] px-6 py-4 text-lg font-semibold text-[#FFFCEF] transition hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50"
            >
              <span className="invisible w-5 text-xl leading-none">{'\u2190'}</span>
              <span>{isSaving ? 'Saving…' : primaryActionLabel}</span>
              <span className="w-5 text-xl leading-none">{'\u2192'}</span>
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ScheduleUnitModal;
